import React from "react"
import type { ReactNode } from "react"
import { createRoot } from "react-dom/client"
import ConfirmModal from "./confirm-modal"
import Action from "./action"

/**
 * == Example
 *
 * const result = await confirm("Are you sure?", { confirm: "Yes", cancel: "No"})
 *
 * @param label message to display in the body of the modal.
 * @param options
 * @param options.message longer message for the body of the modal
 * @param options.confirm text in the confirm button
 * @param options.cancel text in the cancel button
 * @param options.dismissable close when background is clicked
 */

type ConfirmOptions = {
  message?: ReactNode
  confirm?: string
  cancel?: string
  dismissable?: boolean
  picture?: ReactNode
}

async function confirm(
  label: ReactNode,
  {
    confirm: confirmText = "Yes",
    cancel = "No",
    message,
    dismissable = true,
    picture
  }: ConfirmOptions = {}
): Promise<boolean> {
  return new Promise(function (resolve) {
    const container = document.createElement("div")
    const root = createRoot(container)

    const close = (result: boolean) => {
      root.unmount()
      container.remove()
      resolve(result)
    }

    const handleCancel = () => {
      close(false)
    }

    const handleConfirm = () => {
      close(true)
    }

    const dialog = (
      <ConfirmModal
        picture={picture}
        onClose={handleCancel}
        label={label}
        dismissable={dismissable}
      >
        {message}
        <Action onClick={handleConfirm}>{confirmText}</Action>
        <Action variant="secondary" onClick={handleCancel}>
          {cancel}
        </Action>
      </ConfirmModal>
    )

    root.render(dialog)

    document.body.appendChild(container)
  })
}

export default confirm
